import React, { useContext } from 'react'
import { ReservationProps } from '../../../../interfaces'
import ICamper from '../../../../interfaces/ICamper'
import moment from 'moment'
import pluralize from '../../../../utils/pluralize'
import { CamperBlocked } from '../../../ui/StatusBadges'
import { formatName } from '../../../../utils/formatName'
import createMarkup from '../../../../utils/createMarkup'
import avatarFor from '../../../../utils/avatarFor'
import generateAvatar from '../../../../utils/generateAvatar'
import generateConsistentNaturalHexColor from '../../../../utils/generateNaturalHexColor'
import { formatPhoneNumber } from '../../../../utils'
import { AdminContext } from '../../../../contexts/AdminContext'
import classNames from '../../../../utils/classNames'
import IPaymentMethods from '../../../../interfaces/IPaymentMethods'
import { CreditCardIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid'
import HoverTip from '../../../ui/HoverTip'
import formatLengthUnit from '~/utils/formatLengthUnit'

export const checkIsCamperEmpty = (camper: ICamper) => {
  if (!camper) return true

  return (
    !camper?.first_name &&
    !camper?.last_name &&
    !camper?.email &&
    !camper?.phone &&
    !camper?.address &&
    !camper?.vehicle_rig_length &&
    !camper?.vehicle_year &&
    !camper?.vehicle_color &&
    !camper?.vehicle_make &&
    !camper?.vehicle_rig_type &&
    !camper?.vehicle_electric &&
    !camper?.license_plate &&
    !camper?.default_pets_count &&
    !camper?.notes
  )
}

const hasAdditionalVehicleInfo = (camper: ICamper) => {
  return (
    camper.additional_vehicle_make ||
    camper.additional_vehicle_year ||
    camper.additional_vehicle_color ||
    camper.additional_vehicle_license_plate
  )
}

const hasEmergencyContactInfo = (camper: ICamper) => {
  return (
    camper.emergency_contact_name ||
    camper.emergency_contact_relationship ||
    camper.emergency_contact_email ||
    camper.emergency_contact_phone
  )
}

interface ComponentProps {
  camper: ICamper
  setSelectedIndex?: (index: number) => void
  isBlocked?: boolean
  reservation?: ReservationProps
  isCamperLoading?: boolean
  readOnly?: boolean
  paymentMethods?: IPaymentMethods[]
  hidePaymentMethods?: boolean
  hideCardBorder?: boolean
  hideAdditionalInfo?: boolean
  hideAvatar?: boolean
}

const ProfileCard = ({
  camper,
  reservation,
  setSelectedIndex,
  isBlocked,
  isCamperLoading,
  readOnly,
  paymentMethods,
  hidePaymentMethods = false,
  hideCardBorder = false,
  hideAdditionalInfo = false,
  hideAvatar = false,
}: ComponentProps) => {
  const { setSelectedCamper, setCurrentPage, camp } = useContext(AdminContext)

  if (isCamperLoading) {
    return (
      <div className="flex flex-row items-center bg-white rounded-lg border border-dotted border-gray-200 px-4.5 py-4.5 animate-pulsing">
        <div className="w-16 h-16 mr-4 flex-shrink-0 rounded-full bg-gray-200" />
        <div className="flex flex-col space-y-3 w-full">
          <div className="flex justify-between">
            <div className="flex flex-col space-y-1 w-full">
              <div className="bg-gray-200 font-medium h-5 rounded-md w-full"></div>
              <div className="bg-gray-200 h-5 rounded-md w-1/2"></div>
              <div className="bg-gray-200 h-5 rounded-md w-1/3"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!camper || checkIsCamperEmpty(camper)) {
    return (
      <div className="flex flex-col items-center bg-white rounded-lg border border-gray-200 px-4.5 py-4.5">
        <div className="text-gray-300 text-md">No camper information to show</div>
      </div>
    )
  }

  const mostRecentReservation =
    reservation ||
    (camper.reservations || []).sort((a, b) => {
      const momentA = moment(a.created_at)
      const momentB = moment(b.created_at)

      if (momentA.isBefore(momentB)) {
        return 1
      } else if (momentA.isAfter(momentB)) {
        return -1
      } else {
        return 0
      }
    })[0]

  let vehicleInfo = [
    camper.vehicle_rig_length &&
      (camper.vehicle_rig_length.includes("ft") || camper.vehicle_rig_length.includes("m")
        ? camper.vehicle_rig_length
        : camper.vehicle_rig_length + " " + formatLengthUnit(camp.length_unit)),
    camper.vehicle_year && camper.vehicle_year,
    camper.vehicle_color && camper.vehicle_color,
    camper.vehicle_make && camper.vehicle_make,
  ]
    .filter((x) => x)
    .join(" ")

  if (camper.vehicle_rig_type && camper.vehicle_electric) {
    vehicleInfo += " - " + camper.vehicle_rig_type + ", " + camper.vehicle_electric + "-amp"
  } else if (camper.vehicle_rig_type) {
    vehicleInfo += " - " + camper.vehicle_rig_type
  } else if (camper.vehicle_electric) {
    vehicleInfo += " - " + camper.vehicle_electric + "-amp"
  }

  if (vehicleInfo.startsWith(" - ")) {
    vehicleInfo = vehicleInfo.slice(3)
  }

  const formatPetsInfo = (petsInfo: string) => {
    if (/^\d+$/.test(petsInfo)) {
      return petsInfo + pluralize(" pet", Number(petsInfo))
    } else {
      return petsInfo
    }
  }

  const additionalVehicleInfo = [
    camper.additional_vehicle_make && camper.additional_vehicle_make,
    camper.additional_vehicle_year && camper.additional_vehicle_year,
    camper.additional_vehicle_color && camper.additional_vehicle_color,
  ]
    .filter((x) => x)
    .join(" ")


  const passengerInfo =
    camper.default_adults_count || camper.default_kids_count || camper.default_pets_info
      ? [
          camper?.default_adults_count &&
            camper?.default_adults_count +
              (camper?.default_kids_count
                ? pluralize(" adult", camper?.default_adults_count)
                : pluralize(" passenger", camper?.default_adults_count)),
          camper?.default_kids_count &&
            camper?.default_kids_count + (camper?.default_kids_count > 1 ? " children" : " child"),
          formatPetsInfo(camper.default_pets_info),
        ]
          .filter((x) => x)
          .join(", ")
      : ""

  return (
    <div
      className={classNames(
        "flex flex-row",
        !hideCardBorder && "bg-white rounded-lg border border-gray-200 px-4.5 pt-4 pb-3.5",
        !readOnly && "cursor-pointer hover:bg-gray-50",
      )}
      onClick={() => {
        if (readOnly) return

        setSelectedCamper(camper)
        setCurrentPage("guests")
      }}
      data-testid="profile-card"
    >
      {!hideAvatar && (
        <div className="w-16 h-16 mr-4 flex-shrink-0">
          {avatarFor(
            generateAvatar(camper.first_name + camper.last_name || camper.email || camper.phone),
            generateConsistentNaturalHexColor(camper.first_name + camper.last_name || camper.email || camper.phone),
          )}
        </div>
      )}
      <div className="flex flex-col space-y-3 w-full">
        <div className="flex justify-between">
          <div className="flex flex-col space-y-1">
            <div className="text-gray-800 text-lg leading-5 font-medium">{formatName(camper)}</div>
            {isBlocked && <CamperBlocked />}
            <div className="text-gray-450 text-md leading-4">{camper.email}</div>
            <div className="text-gray-450 text-md leading-4">{formatPhoneNumber(camper.phone_country_code + camper.phone_number)}</div>
            <div className="text-gray-450 text-md leading-4">{camper.address}</div>
            <div className="text-gray-450 text-md leading-4">{camper.company_name}</div>

            {!hidePaymentMethods && (
              <div className="sm:hidden">
                <PaymentMethodsSection paymentMethods={paymentMethods || []} camper={camper} />
              </div>
            )}
          </div>
          {!hidePaymentMethods && (
            <div className="hidden sm:inline">
              <PaymentMethodsSection paymentMethods={paymentMethods || []} camper={camper} />
            </div>
          )}
        </div>

        {(vehicleInfo || passengerInfo) && !hideAdditionalInfo && (
          <>
            <div className="w-full border-b border-gray-200" />
            <div className="flex flex-col text-md leading-5">
              <div>{vehicleInfo}</div>
              {camper.license_plate && <div>License #: {camper.license_plate}</div>}
              {passengerInfo && <div>{passengerInfo}</div>}
            </div>
          </>
        )}

        {hasAdditionalVehicleInfo(camper) && !hideAdditionalInfo && (
          <>
            <div className="w-full border-b border-gray-200" />
            <div className="flex flex-col text-md leading-5">
              <div>{additionalVehicleInfo}</div>
              {camper.additional_vehicle_license_plate && (
                <div>License #: {camper.additional_vehicle_license_plate}</div>
              )}
            </div>
          </>
        )}

        {hasEmergencyContactInfo(camper) && !hideAdditionalInfo && (
          <>
            <div className="w-full border-b border-gray-200" />
            <div className="flex flex-col text-md leading-5">
              <div>
                Emergency Contact: {camper.emergency_contact_name}
                {camper.emergency_contact_relationship && " - " + camper.emergency_contact_relationship}
              </div>
              {camper.emergency_contact_email && <div>{camper.emergency_contact_email}</div>}
              {camper.emergency_contact_phone && <div>{formatPhoneNumber(camper.emergency_contact_phone)}</div>}
            </div>
          </>
        )}

        {camper.notes && !hideAdditionalInfo && (
          <>
            <div className="w-full border-b border-gray-200" />
            <div className="flex flex-col text-md leading-5 notes-text">
              <div dangerouslySetInnerHTML={createMarkup(camper.notes)} />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

interface PaymentMethodsSectionProps {
  paymentMethods: IPaymentMethods[]
  camper: ICamper
}

const PaymentMethodsSection = ({ paymentMethods, camper }: PaymentMethodsSectionProps) => {
  return (
    <div>
      {paymentMethods && paymentMethods.length > 0 ? (
        <div className='p-2 flex flex-col items-end rounded-md border border-gray-200 w-min'>
          <div className='flex items-center'>
            <CreditCardIcon className='mr-2 w-5 h-5 text-gray-400 flex-shrink-0' />
            <div className='mb-1 text-gray-400 font-semibold text-sm leading-4 whitespace-nowrap'>
              {paymentMethods.length} payment {pluralize('method', paymentMethods.length)}
            </div>
          </div>
          {/* TODO: Last 4 digits of card doesn't come through */}
          {paymentMethods[0]?.last4 && (
            <div className='text-gray-600 font-semibold leading-4'>
              •••• {paymentMethods[0]?.last4}
            </div>
          )}
        </div>
      ) : (
        <HoverTip content='This guest cannot be autocharged' wrap={false} xs>
          <div className='p-2 flex flex-row items-center rounded-md border border-gray-200'>
            <ExclamationTriangleIcon className='mr-2 w-5 h-5 text-gray-400' />
            <div className='text-gray-400 font-semibold text-sm leading-4'>0 payment methods</div>
          </div>
        </HoverTip>
      )}
    </div>
  )
}

export default ProfileCard
